import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	CommentWithRepliesModel,
	HubCommentSection,
	HubCommentSectionService
} from "./HubCommentSection";
import {
	ensureNotNil,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	Anchor,
	BusyIndicator,
	FormattedText,
	NoResults
} from "client/Components";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { TimeZone } from "client/DateAndTime/timezones";
import { localize } from "client_legacy/ndaw/culture";
import { MobileComment, MobileCommentFormHandle } from "./MobileComment/MobileComment";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { useImageViewer } from "client/Features/Hub/useImageViewer";
import { HubCommentModel, HubCommentStatisticsModel } from "client/Api/Hub/common";
import { getStructuredComments } from "client/Features/Hub/common";
import {
	useWorkspaceMediaQuery,
	MediaQueryKind,
	useForceRerender,
	NumberFormatter
} from "client/Hooks";
import { useCommentNavigation } from "./hooks/useCommentNavigation";
import { useMobileCommentState } from "./hooks/useMobileComment";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { HttpRequestError } from "client/Api/httpClient";
import { never } from "client_legacy/ndaw/errors";
import styles from "./CommentThread.module.less";
import { useHubStatisticsFormatter } from "../useHubStatisticsFormatter";

export type CommentThreadProps = {
	onMobileCommentIsEditingChanged: (isEditing: boolean) => void;
	onStatisticsUpdate: () => void;
	commentId: number;
	options: HubCommentOptionsModel;
	timezone: TimeZone;
	onAllCommentView: () => void;
	allCommentsCount: number;
	service: HubCommentSectionService;
	getRelatedCommentThread: (
		commentId: number,
		signal?: AbortSignal
	) => Promise<readonly HubCommentModel[]>;
};

export const CommentThread = (props: CommentThreadProps): ReactElement => {
	const {
		onMobileCommentIsEditingChanged,
		onStatisticsUpdate,
		onAllCommentView,
		commentId,
		options,
		timezone,
		allCommentsCount,
		service,
		getRelatedCommentThread
	} = props;

	const [commentState, setCommentState] = useState<CommentState>({ status: "loading" });
	const [commentIdToNavigateTo, setCommentIdToNavigateTo] = useState<number | undefined>(undefined);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [imageViewerDialog, viewImageHandler] = useImageViewer();
	const mobileCommentFormRef: RefObject<MobileCommentFormHandle | null> = useRef<MobileCommentFormHandle>(null);
	const [reload, dataVersion] = useForceRerender();

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const currentUserInfo: UserIdentityModel | null = ensureNotNil(
		useUserProfileStore(x => x.currentUserIdentity)
	);

	const structuredComments = useMemo(
		(): readonly CommentWithRepliesModel[] => {
			if (commentState.status !== "loaded") {
				return [];
			}

			return getStructuredComments(commentState.items);
		},
		[commentState]
	);

	const formFocusHandler = useCallback(
		(): void => {
			setTimeout(
				(): void => {
					mobileCommentFormRef.current
						?.focusInput();
				},
				0
			);
		},
		[]
	);

	const {
		commentListRef,
		navigateToCommentIdHandler
	} = useCommentNavigation();

	const {
		mobileMode,
		initMobileMode,
		mobileModeChangeHandler,
	} = useMobileCommentState(
		formFocusHandler
	);

	const setLoadedComments = useCallback(
		(
			updateCallback: (comments: readonly HubCommentModel[]) => readonly HubCommentModel[]
		): void => {
			setCommentState(
				(commentState: CommentState): CommentState => {
					if (commentState.status !== "loaded") {
						throw new Error("Comments are not loaded");
					}

					return {
						...commentState,
						items: updateCallback(commentState.items)
					};
				}
			);
		},
		[]
	);

	const commentDeletedHandler = useCallback(
		(commentId: number): void => {
			if (commentState.status !== "loaded") {
				throw new Error("Cannot delete comments while it's not loaded");
			}

			const commentToDelete: HubCommentModel | undefined = commentState.items
				.find(x => x.id === commentId);

			if (isNil(commentToDelete)) {
				throw new Error(`Comment with id: ${commentId} could not be found`);
			}

			if (isNil(commentToDelete.parentCommentId)) {
				onStatisticsUpdate();
				onAllCommentView();
			}

			setLoadedComments(
				(x: readonly HubCommentModel[]): readonly HubCommentModel[] => {
					return x.filter(c => c.id !== commentId);
				}
			);
		},
		[
			commentState,
			setLoadedComments,
			onStatisticsUpdate,
			onAllCommentView
		]
	);

	const commentStatisticsUpdatedHandler = useCallback(
		(
			commentId: number,
			statistics: HubCommentStatisticsModel
		): void => {
			setLoadedComments(
				(x: readonly HubCommentModel[]): readonly HubCommentModel[] => {
					return x.map(
						c => c.id === commentId
							? {
								...c,
								statistics
							}
							: c
					);
				}
			);
		},
		[setLoadedComments]
	);

	const commentUpdatedHandler = useCallback(
		(comment: HubCommentModel): void => {
			setLoadedComments(
				(x: readonly HubCommentModel[]): readonly HubCommentModel[] => {
					return x.map(
						c => c.id === comment.id
							? comment
							: c
					);
				}
			);
		},
		[setLoadedComments]
	);

	const commentCreatedHandler = useCallback(
		(comment: HubCommentModel): void => {
			setLoadedComments(
				(x: readonly HubCommentModel[]): readonly HubCommentModel[] => {
					return [
						...x,
						comment
					];
				}
			);

			setCommentIdToNavigateTo(comment.id);
		},
		[setLoadedComments]
	);

	const viewImageByUrlHandler = useCallback(
		(imageUrl: string): void => {
			viewImageHandler({
				url: imageUrl
			});
		},
		[viewImageHandler]
	);

	useEffect(
		(): () => void => {
			hasImplicitDependencyOn(dataVersion);

			async function load(signal: AbortSignal): Promise<void> {
				setCommentState({ status: "loading" });

				try {
					const items: readonly HubCommentModel[] = await getRelatedCommentThread(
						commentId,
						signal
					);

					setCommentState({
						status: "loaded",
						items
					});

					setCommentIdToNavigateTo(commentId);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setCommentState({
						status: e instanceof HttpRequestError && e.statusCode === 404
							? "not-found"
							: "error"
					});
				}
			}

			const cancellation = new AbortController();
			load(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			commentId,
			dataVersion,
			getRelatedCommentThread
		]
	);

	useEffect(
		(): void => {
			if (!commentIdToNavigateTo) {
				return;
			}
			navigateToCommentIdHandler(commentIdToNavigateTo);
			setCommentIdToNavigateTo(undefined);
		},
		[
			commentIdToNavigateTo,
			navigateToCommentIdHandler,
			structuredComments
		]
	);

	const hasReplies: boolean = structuredComments.length > 0;

	const renderContent = (): ReactElement => {
		return (
			<>
				<div
					{...setTrackingId("comment-thread")}
				>
					<HubCommentSection>
						<div className={styles.header}>
							<h4 className={styles.headerText}>
								{hasReplies ? localize("_CommentThread") : localize("_Comments")}
							</h4>
							<Anchor
								className={styles.viewAllCommentsLink}
								onClick={onAllCommentView}
							>
								<FormattedText
									text={localize("_StatusAmountFormat")}
									thingsToReplace={
										[
											{
												what: "{{Status}}",
												filler: localize("_ViewAllComments")
											},
											{
												what: "{{Amount}}",
												filler: statisticsFormatter(allCommentsCount)
											}
										]
									}
								/>
							</Anchor>
						</div>
						<HubCommentSection.CommentList
							ref={commentListRef}
							comments={structuredComments}
							onStatisticsUpdated={commentStatisticsUpdatedHandler}
							onUpdated={commentUpdatedHandler}
							onDeleted={commentDeletedHandler}
							onCreated={commentCreatedHandler}
							isMobile={isMobile}
							mobileModeChangeHandler={mobileModeChangeHandler}
							mobileMode={mobileMode}
							service={service}
							currentUserInfo={currentUserInfo}
							timezone={timezone}
							viewImageHandler={viewImageByUrlHandler}
							commentOptions={options}
						/>
						{isMobile && mobileMode.kind !== "create" && (
							<MobileComment
								currentUserInfo={currentUserInfo}
								onCreated={commentCreatedHandler}
								mode={mobileMode}
								onCancel={initMobileMode}
								onUpdated={commentUpdatedHandler}
								ref={mobileCommentFormRef}
								onMobileCommentIsEditingChanged={onMobileCommentIsEditingChanged}
								service={service}
								options={options}
							/>
						)}
					</HubCommentSection>
				</div>
				{imageViewerDialog}
			</>
		);
	};

	switch (commentState.status) {
		case "loading":
			return (
				<div className={styles.loadMoreIndicatorContainer}>
					<BusyIndicator />
				</div>
			);
		case "error":
			return (
				<NoResults
					icon="gear"
					title={localize("_NotAbleToRetrieveComment")}
					message={localize("_TheCommentCouldNotBeRetrieved")}
					onRetry={reload}
				/>
			);
		case "not-found":
			return (
				<NoResults
					icon="gear"
					title={localize("_CommentNotFound")}
					message={localize("_TheCommentMayHaveBeenDeleted")}
					retryText={localize("_ViewAllComments")}
					onRetry={onAllCommentView}
				/>
			);
		case "loaded":
			return renderContent();

		default:
			never(commentState);
	}
};
CommentThread.displayName = "CommentThread";

type CommentState = {
	status: "loading" | "error" | "not-found";
} | {
	status: "loaded";
	items: readonly HubCommentModel[];
};
